import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import {
  MonthlyGallonsData,
  PerTruckData,
  VendorBreakdownData,
  StateBreakdownData,
  YardVsRoadData,
  ReportGenerateRequest,
} from '../interfaces';
import { AnalyticsService } from '../services/analytics.service';
import { ReportService } from '../services/report.service';

interface MonthOption {
  label: string;
  value: number;
}

interface YearOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
})
export class AdminComponent implements OnInit {
  // Report regeneration form
  selectedYear: number = new Date().getFullYear();
  selectedMonth: number = new Date().getMonth() === 0 ? 12 : new Date().getMonth();
  overrideDropbox = false;
  emailReport = false;
  downloadReport = false;
  isGenerating = false;
  emailTo: string[] = ['gschmit@schtrk.com'];
  emailCc: string[] = ['info-tech@schtrk.com'];

  monthOptions: MonthOption[] = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];

  yearOptions: YearOption[] = [];

  // Analytics filter
  filterYear: number | undefined = new Date().getFullYear();
  filterMonth: number | undefined = undefined;

  // Chart data
  monthlyGallonsData: any = {};
  perTruckData: any = {};
  vendorBreakdownData: any = {};
  stateBreakdownData: any = {};
  yardVsRoadData: any = {};

  // Each chart gets its own options object so Chart.js mutations on one
  // instance don't bleed into the others via a shared reference.
  monthlyGallonsOptions: any = this.makeChartOptions();
  perTruckOptions: any = this.makeChartOptions();
  vendorBreakdownOptions: any = this.makeChartOptions();
  stateBreakdownOptions: any = this.makeChartOptions();
  yardVsRoadOptions: any = this.makeChartOptions();

  isLoadingCharts = false;

  private makeChartOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' as const },
      },
    };
  }

  constructor(
    private analyticsService: AnalyticsService,
    private reportService: ReportService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.buildYearOptions();
    this.loadAnalytics();
  }

  buildYearOptions(): void {
    const currentYear = new Date().getFullYear();
    for (let y = currentYear; y >= currentYear - 5; y--) {
      this.yearOptions.push({ label: String(y), value: y });
    }
  }

  loadAnalytics(): void {
    this.isLoadingCharts = true;
    const year = this.filterYear;
    const month = this.filterMonth;

    Promise.all([
      this.analyticsService.getMonthlyGallons(year).toPromise(),
      this.analyticsService.getPerTruck(year, month).toPromise(),
      this.analyticsService.getVendorBreakdown(year, month).toPromise(),
      this.analyticsService.getStateBreakdown(year, month).toPromise(),
      this.analyticsService.getYardVsRoad(year, month).toPromise(),
    ])
      .then(([monthly, perTruck, vendor, state, yardVsRoad]) => {
        this.buildMonthlyGallonsChart(monthly || []);
        this.buildPerTruckChart(perTruck || []);
        this.buildVendorBreakdownChart(vendor || []);
        this.buildStateBreakdownChart(state || []);
        this.buildYardVsRoadChart(yardVsRoad || []);
        this.isLoadingCharts = false;
      })
      .catch(err => {
        console.error('Error loading analytics:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load analytics data.',
        });
        this.isLoadingCharts = false;
      });
  }

  buildMonthlyGallonsChart(data: MonthlyGallonsData[]): void {
    const MONTH_NAMES = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    this.monthlyGallonsData = JSON.parse(JSON.stringify({
      labels: data.map(d => `${MONTH_NAMES[d.month]} ${d.year}`),
      datasets: [{
        label: 'Total Gallons',
        data: data.map(d => d.total_gallons),
        fill: false,
        tension: 0.4,
        borderColor: '#3B82F6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
      }],
    }));
  }

  buildPerTruckChart(data: PerTruckData[]): void {
    const top15 = data.slice(0, 15);
    this.perTruckData = JSON.parse(JSON.stringify({
      labels: top15.map(d => d.truck_number || 'Unknown'),
      datasets: [{
        label: 'Total Gallons',
        data: top15.map(d => d.total_gallons),
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
        borderColor: '#6366F1',
        borderWidth: 1,
      }],
    }));
  }

  buildVendorBreakdownChart(data: VendorBreakdownData[]): void {
    const colors = [
      '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
      '#EC4899', '#14B8A6', '#F97316', '#84CC16', '#06B6D4',
    ];
    this.vendorBreakdownData = JSON.parse(JSON.stringify({
      labels: data.map(d => d.vendor || 'Unknown'),
      datasets: [{
        data: data.map(d => d.total_gallons),
        backgroundColor: data.map((_, i) => colors[i % colors.length]),
      }],
    }));
  }

  buildStateBreakdownChart(data: StateBreakdownData[]): void {
    this.stateBreakdownData = JSON.parse(JSON.stringify({
      labels: data.map(d => d.state || 'Unknown'),
      datasets: [{
        label: 'Total Gallons',
        data: data.map(d => d.total_gallons),
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
        borderColor: '#10B981',
        borderWidth: 1,
      }],
    }));
  }

  buildYardVsRoadChart(data: YardVsRoadData[]): void {
    const chartData = {
      labels: data.map(d => d.period),
      datasets: [
        {
          label: 'Yard Fills',
          data: data.map(d => d.yard_gallons),
          backgroundColor: 'rgba(245, 158, 11, 0.7)',
          borderColor: '#F59E0B',
          borderWidth: 1,
        },
        {
          label: 'External Fills',
          data: data.map(d => d.road_gallons),
          backgroundColor: 'rgba(59, 130, 246, 0.7)',
          borderColor: '#3B82F6',
          borderWidth: 1,
        },
      ],
    };
    // Deep-copy forces PrimeNG p-chart to detect the reference change and redraw
    this.yardVsRoadData = JSON.parse(JSON.stringify(chartData));
  }

  hasData(chartData: any): boolean {
    return !!(chartData?.datasets?.[0]?.data?.length);
  }

  onFilterChange(): void {
    this.loadAnalytics();
  }

  onGenerateReport(): void {
    if (!this.selectedYear || !this.selectedMonth) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validation',
        detail: 'Please select a year and month.',
      });
      return;
    }

    const request: ReportGenerateRequest = {
      year: this.selectedYear,
      month: this.selectedMonth,
      override_dropbox: this.overrideDropbox,
      email_report: this.emailReport,
      download: this.downloadReport,
      email_to: this.emailTo,
      email_cc: this.emailCc,
    };

    this.isGenerating = true;

    if (this.downloadReport) {
      this.reportService.downloadReport(request).subscribe({
        next: (blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          const month = this.monthOptions.find(m => m.value === this.selectedMonth);
          a.href = url;
          a.download = `Fuel ${month?.label} ${this.selectedYear}.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Report downloaded.',
          });
          this.isGenerating = false;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to generate report.',
          });
          this.isGenerating = false;
        },
      });
    } else {
      this.reportService.generateReport(request).subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: response.message,
          });
          this.isGenerating = false;
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to generate report.',
          });
          this.isGenerating = false;
        },
      });
    }
  }
}
